import React, { useState } from 'react';
import { Avatar, Box, lighten, useMediaQuery, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { SkeletonLoader, HeaderSkeleton } from '@europrocurement/flexy-components';
import { useSelector } from 'react-redux';
import { DossierOpenSearch, FormalityStateType } from '@europrocurement/l2d-domain';
import { HeaderTitleWithEdit } from './fragments/HeaderTitleWithEdit';
import { StatusWithChip } from './fragments/StatusWithChip';
import { MainInfoSection } from './fragments/MainInfoSection';
import { PrescripteurOffreSection } from './fragments/PrescripteurOffreSection';
import { UpdateDossierPayload } from '../../../types/UpdateDossierPayload';

const themeColor = ['primary', 'secondary', 'warning', 'success', 'error'] as const;
type ThemeColor = (typeof themeColor)[number];
type HexColor = string;

export type DetailDossierTitleProps = {
    dossier: {
        statut: string;
        statutId: number;
        societeRef?: string;
    };
    prescripteur: {
        id: number;
        raisonSociale: string;
        codeComptable: string;
        urlToDetails?: string;
    };
    offre: { libelle: string };
    clientOpere: { raisonSociale: string; siren?: string; urlToSocieteDotCom?: string; id: number };
    icon?: IconName;
    color?: ThemeColor | HexColor;
    refreshDossier: () => void;
    updateDossier: (payload: UpdateDossierPayload) => void;
};

const DetailDossierTitle: React.FunctionComponent<DetailDossierTitleProps> = function ({
    dossier,
    prescripteur,
    offre,
    clientOpere,
    icon = 'folder',
    color = 'primary',
    refreshDossier,
    updateDossier,
}) {
    const isThemeColor = (str: string): str is ThemeColor => themeColor.includes(str as ThemeColor);
    const { palette, breakpoints } = useTheme();
    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));
    const baseColor = isThemeColor(color) ? palette[color].main : color;

    // État pour gérer les skeleton lors des actions de mise à jour
    const [isRefreshing, setIsRefreshing] = useState(false);

    const dossierSelected: DossierOpenSearch | undefined = useSelector(
        (s: FormalityStateType) => s.formalites.dossiersOpenSearch.main.selected,
    );

    // Gestion des actions de mise à jour avec skeleton
    const handleRefreshWithSkeleton = React.useCallback(async () => {
        setIsRefreshing(true);
        try {
            await refreshDossier();
        } catch (error) {
            console.error('Erreur lors du refresh:', error);
        } finally {
            setTimeout(() => setIsRefreshing(false), 1500); // Délai pour l'animation du skeleton
        }
    }, [refreshDossier]);

    const handleUpdateWithSkeleton = React.useCallback(
        async (payload: UpdateDossierPayload) => {
            setIsRefreshing(true);
            try {
                await updateDossier(payload);
                await new Promise((resolve) => {
                    setTimeout(resolve, 1500);
                });
                await refreshDossier();
            } catch (error) {
                console.error('Erreur lors de la mise à jour:', error);
            } finally {
                setTimeout(() => setIsRefreshing(false), 1500);
            }
        },
        [updateDossier, refreshDossier],
    );

    const avatarStyle = {
        display: 'flex',
        borderRadius: '8px',
        backgroundColor: lighten(baseColor, 0.92),
        minHeight: '75px',
        minWidth: '75px',
    };

    if (downMdBreakpoint) {
        avatarStyle.display = 'none';
    }

    const isLoading = isRefreshing;

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={<HeaderSkeleton />}
            containerSx={{ marginBottom: '16px' }}
        >
            <Box sx={{ display: 'flex', gap: '0 24px' }}>
                <Avatar sx={avatarStyle}>
                    <FaOptionIcon
                        icon={['fasl', icon]}
                        color={color}
                    />
                </Avatar>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                        }}
                    >
                        {/* Titre avec bouton d'édition au hover */}
                        <HeaderTitleWithEdit
                            clientOpereRaisonSociale={clientOpere.raisonSociale}
                            sousClientSelected={clientOpere.id}
                            onSuccess={handleRefreshWithSkeleton}
                        />

                        <StatusWithChip
                            statutId={dossier.statutId}
                            statut={dossier.statut}
                        />
                    </Box>

                    {/* Informations complémentaires */}
                    <MainInfoSection
                        clientOpere={clientOpere}
                        dossier={dossier}
                        dossierSelected={dossierSelected}
                        palette={palette}
                        onUpdate={handleUpdateWithSkeleton}
                        onRefresh={handleRefreshWithSkeleton}
                    />

                    {/* Prescripteur et offre */}
                    <PrescripteurOffreSection
                        prescripteur={prescripteur}
                        offre={offre}
                        palette={palette}
                    />
                </Box>
            </Box>
        </SkeletonLoader>
    );
};

export default DetailDossierTitle;
